import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import type { StudioId } from '@/domain/types';
import InfoTip from '@/components/InfoTip';
import { ReportBarChart } from '@/components/reporting/ReportChart';
import { REPORT_COLORS } from '@/components/reporting/reportChartTheme';
import {
  customerGeography,
  newVsReturningByDay,
  lapsedRegularsCount,
} from '@/domain/reportingEngine';

function dayLabel(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00+10:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' });
}

interface Props {
  /** The selected date-range window for the new-vs-returning chart. */
  days: readonly string[];
  /** Studio scope, or undefined for all studios. */
  studioId?: StudioId;
}

/**
 * Customers tab (Prompt F, Stage 5). Geography by Melbourne area (horizontal
 * bars, no map), new vs returning over the range, and the lapsed-regulars count
 * nurture re-engagement feeds on.
 */
export default function CustomersTab({ days, studioId }: Props) {
  const customers = useBrainStore((s) => s.customers);
  const bookings = useBrainStore((s) => s.bookings);

  // States present in the scoped customer book, for the geography filter. VIC
  // first so Melbourne's compass-area breakdown is the default view.
  const states = useMemo(() => {
    const set = new Set<string>();
    for (const c of Object.values(customers)) {
      if (studioId && c.primaryStudioId !== studioId) continue;
      set.add(c.state);
    }
    return [...set].sort((a, b) => (a === 'VIC' ? -1 : b === 'VIC' ? 1 : a.localeCompare(b)));
  }, [customers, studioId]);
  const [stateFilter, setStateFilter] = useState<string>('VIC');
  const activeState = states.includes(stateFilter) ? stateFilter : (states[0] ?? 'VIC');

  const geography = useMemo(
    () => customerGeography(studioId ?? null, activeState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customers, studioId, activeState],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const lapsed = useMemo(() => lapsedRegularsCount(studioId), [studioId, bookings, customers]);

  const newReturning = useMemo(() => {
    return newVsReturningByDay(days, studioId).map((r) => ({
      day: dayLabel(r.dayIso),
      New: r.newCount,
      Returning: r.returningCount,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, studioId, bookings]);

  const geoMax = Math.max(1, ...geography.map((g) => g.count));

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-customers">
      {/* Lapsed regulars */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div
          className={clsx('panel-dark p-4', lapsed > 0 && 'border-l-2 border-blood/50')}
          data-testid="customers-lapsed"
        >
          <p className="label-small-caps flex items-center gap-1">
            Lapsed regulars
            <InfoTip>
              Customers whose gap since their last completed session now exceeds 1.5× their own historical average
              gap (&quot;came every six weeks, now eight&quot;). Needs at least two past sessions to have an average.
              Nurture re-engagement feeds on this count.
            </InfoTip>
          </p>
          <p className={clsx('font-cinzel text-3xl mt-2', lapsed > 0 ? 'text-blood' : 'text-mint')}>{lapsed}</p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
            Gap now &gt; 1.5× their own average — re-engagement targets
          </p>
        </div>
      </section>

      {/* Geography by area, with a state filter */}
      <section className="panel p-4" data-testid="customers-geography">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h2 className="heading-serif text-lg text-cream">
            Where customers come from
            <InfoTip>
              Customer count by home area. VIC customers bucket into the five Melbourne compass areas (North, East,
              South, West, South-east) by suburb; interstate customers group by their city. Filter by state at right.
            </InfoTip>
          </h2>
          <label className="flex items-center gap-2">
            <span className="label-small-caps text-gold-bright">State</span>
            <select
              data-testid="customers-state-filter"
              value={activeState}
              onChange={(e) => setStateFilter(e.target.value)}
              className="bg-ink-soft/40 border border-gold-dim/30 rounded-md px-2 py-1 text-sm text-cream"
            >
              {states.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          {activeState === 'VIC' ? 'By Melbourne compass area' : `By city — ${activeState}`}
        </p>
        {geography.length === 0 ? (
          <p className="font-serif text-cream-soft text-sm">No customers yet.</p>
        ) : (
          <ul className="space-y-2">
            {geography.map((g) => (
              <li key={g.region} className="flex items-center gap-3" data-testid={`customers-geo-${g.region}`}>
                <span className="w-28 shrink-0 font-inter text-sm text-cream truncate">{g.region}</span>
                <div className="flex-1 h-4 rounded bg-ink-soft/40 overflow-hidden">
                  <div
                    className="h-full bg-gold-bright/70 rounded"
                    style={{ width: `${(g.count / geoMax) * 100}%` }}
                  />
                </div>
                <span className="w-8 shrink-0 text-right font-mono text-sm text-cream-soft">{g.count}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* New vs returning per week */}
      <section className="panel p-4" data-testid="customers-new-returning">
        <h2 className="heading-serif text-lg text-cream mb-1">
          New vs returning
          <InfoTip>
            Completed sessions over the selected range, split by whether it was the customer&apos;s first-ever
            completed session (new) or they had an earlier one (returning). Honours the studio filter.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Completed sessions this week, first-timers vs returning
        </p>
        <ReportBarChart
          data={newReturning}
          xKey="day"
          stacked
          series={[
            { key: 'New', name: 'New', color: REPORT_COLORS.mint },
            { key: 'Returning', name: 'Returning', color: REPORT_COLORS.gold },
          ]}
        />
      </section>
    </div>
  );
}
